// /feedback — report a bug or ask for a feature, with no account of any kind.
//
// Auto-mounted at /feedback by the pages routing convention (src/pages/<name>.tsx).
//
// Behind RequireAuth: the edge function requires a session, so sending someone to
// sign in first is honest about it rather than letting them type three paragraphs
// and then bounce off a 401. Self-wrapped in AuthProvider like /login, so the page
// works before a root provider lands (AuthProvider is nest-safe).
import { useState, type CSSProperties, type FormEvent, type JSX } from 'react'
import { AuthProvider, RequireAuth, useAuth } from '../lib/auth'
import {
  submitFeedback,
  type FeedbackKind,
  type FiledIssue,
} from '../lib/feedbackClient'

const KINDS: { value: FeedbackKind; label: string; hint: string }[] = [
  { value: 'bug', label: 'Something is broken', hint: 'It did the wrong thing, or nothing at all.' },
  { value: 'feature', label: 'I want something new', hint: 'An idea, however half-formed.' },
]

const styles: Record<string, CSSProperties> = {
  field: { display: 'flex', flexDirection: 'column', gap: '0.4rem' },
  label: { fontWeight: 600 },
  input: { width: '100%' },
  textarea: { width: '100%', minHeight: '8rem', lineHeight: 1.5, resize: 'vertical' },
  // Radio cards: a 44px+ tap target each, stacked on a phone.
  choice: {
    display: 'flex',
    gap: '0.7rem',
    alignItems: 'flex-start',
    padding: '0.85rem',
    border: '1px solid var(--line)',
    borderRadius: '0.5rem',
    cursor: 'pointer',
  },
  choiceOn: { borderColor: 'var(--accent)', boxShadow: 'inset 0 0 0 1px var(--accent)' },
  radio: { minHeight: 'auto', width: '1.1rem', height: '1.1rem', marginTop: '0.2rem', flex: 'none' },
  submit: {
    minHeight: '3rem',
    padding: '0.75rem 1rem',
    fontWeight: 600,
    borderRadius: '0.5rem',
    border: 'none',
    background: 'var(--accent)',
    color: 'var(--ground)',
  },
  error: { color: '#b3261e', fontWeight: 600 },
}

function FeedbackForm(): JSX.Element {
  const { user } = useAuth()
  const [kind, setKind] = useState<FeedbackKind>('bug')
  const [title, setTitle] = useState('')
  const [details, setDetails] = useState('')
  const [name, setName] = useState('')
  const [sending, setSending] = useState(false)
  const [filed, setFiled] = useState<FiledIssue | null>(null)
  const [error, setError] = useState<string | null>(null)

  async function onSubmit(event: FormEvent): Promise<void> {
    event.preventDefault()
    if (sending) return

    setSending(true)
    setError(null)
    try {
      const issue = await submitFeedback({
        kind,
        title: title.trim(),
        details: details.trim(),
        name: name.trim() || undefined,
      })
      setFiled(issue)
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Submitting failed.')
    } finally {
      setSending(false)
    }
  }

  function reset(): void {
    setFiled(null)
    setTitle('')
    setDetails('')
    setError(null)
  }

  if (filed) {
    return (
      <main className="shell">
        <h1>Filed. Thank you.</h1>
        <p>
          It went in as issue <strong>#{filed.number}</strong> and the organizers can see it now.
          You don&rsquo;t need to do anything else.
        </p>
        <p className="fine">
          Curious?{' '}
          <a href={filed.url} target="_blank" rel="noreferrer noopener external">
            Follow it here
          </a>{' '}
          &mdash; nothing to sign in to.
        </p>
        <div className="actions">
          <button type="button" className="btn" onClick={reset}>
            Send another
          </button>
          <a className="btn" href="/">
            Back to the trip
          </a>
        </div>
      </main>
    )
  }

  return (
    <main className="shell">
      <header className="hero">
        <p className="eyebrow">Feedback</p>
        <h1>Tell us what&rsquo;s wrong, or what&rsquo;s missing</h1>
        <p className="lede">
          This goes straight to the people building SPZ. Nothing to sign up for, no account to
          make &mdash; just write it and send.
        </p>
      </header>

      <form onSubmit={onSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.1rem' }}>
        <fieldset style={{ border: 0, padding: 0, margin: 0, ...styles.field }}>
          <legend style={{ ...styles.label, padding: 0, marginBottom: '0.4rem' }}>
            What kind of thing is this?
          </legend>
          {KINDS.map((option) => (
            <label
              key={option.value}
              style={{
                ...styles.choice,
                ...(kind === option.value ? styles.choiceOn : null),
              }}
            >
              <input
                type="radio"
                name="kind"
                value={option.value}
                checked={kind === option.value}
                onChange={() => setKind(option.value)}
                style={styles.radio}
              />
              <span>
                <strong>{option.label}</strong>
                <br />
                <span className="fine">{option.hint}</span>
              </span>
            </label>
          ))}
        </fieldset>

        <div style={styles.field}>
          <label htmlFor="title" style={styles.label}>
            One line summary
          </label>
          <input
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            required
            maxLength={120}
            style={styles.input}
            placeholder={
              kind === 'bug'
                ? "The dates screen won't save on my phone"
                : 'Let me mark a day as "arriving late"'
            }
          />
        </div>

        <div style={styles.field}>
          <label htmlFor="details" style={styles.label}>
            {kind === 'bug' ? 'What happened?' : 'What would you like?'}
          </label>
          <textarea
            id="details"
            value={details}
            onChange={(e) => setDetails(e.target.value)}
            required
            maxLength={8000}
            style={styles.textarea}
            placeholder={
              kind === 'bug'
                ? 'What you tapped, what you expected, what it did instead. Plain English is fine.'
                : 'What you are trying to do, and why the app makes it hard.'
            }
          />
          <span className="fine">
            {kind === 'bug'
              ? 'We attach which page you were on and what browser you used automatically.'
              : 'However it comes out. Nobody is grading the phrasing.'}
          </span>
        </div>

        <div style={styles.field}>
          <label htmlFor="name" style={styles.label}>
            Your name <span className="fine">(optional)</span>
          </label>
          <input
            id="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={80}
            style={styles.input}
            placeholder="Aunt Marilyn"
          />
          <span className="fine">
            Helps when someone has a follow-up question. Without it we use{' '}
            <strong>{user?.email ?? 'your sign-in email'}</strong>.
          </span>
        </div>

        {error ? (
          <p style={styles.error} role="alert">
            {error}
          </p>
        ) : null}

        <button type="submit" style={styles.submit} disabled={sending}>
          {sending ? 'Sending…' : 'Send it'}
        </button>
      </form>
    </main>
  )
}

export default function FeedbackPage(): JSX.Element {
  return (
    <AuthProvider>
      <RequireAuth>
        <FeedbackForm />
      </RequireAuth>
    </AuthProvider>
  )
}
